import type { CompanyWithUI } from "@/components/shared/CompanyCardHorizontal";
import {
  mapListCompanies,
  mapQuickSearchCompanies,
  type DiscoverShortlistContext,
} from "@/pages/discover/utils/discoverMappers";
import { deriveSearchByFromQuery } from "@/pages/discover/utils/discoverSearch";
import trpc from "@/shared/utils/trpc";
import { queryKeys } from "@/state/queryKeys";

export type DiscoverRouteMode =
  | "facilitatorSearch"
  | "expertSearch"
  | "expoHeadSearch"
  | "ecosystemHeadSearch"
  | "discover"
  | "listAll"
  | "expoDirectory";

export type DiscoverCompaniesResult = {
  companies: CompanyWithUI[];
  totalPages: number;
  matchCount: number;
};

export type DiscoverBrowseParams = {
  mode: DiscoverRouteMode;
  page: number;
  limit: number;
  expoSignupSlug?: string | null;
};

export type DiscoverSearchParams = DiscoverBrowseParams & {
  query: string;
  sectorSlug: string | null;
};

export function discoverBrowseQueryKey(params: DiscoverBrowseParams) {
  return queryKeys.discover.companies({
    kind: "browse",
    mode: params.mode,
    page: params.page,
    limit: params.limit,
    expoSlug: params.expoSignupSlug ?? null,
  });
}

export function discoverSearchQueryKey(params: DiscoverSearchParams) {
  return queryKeys.discover.companies({
    kind: "search",
    mode: params.mode,
    page: params.page,
    limit: params.limit,
    query: params.query,
    sectorSlug: params.sectorSlug,
    expoSlug: params.expoSignupSlug ?? null,
  });
}

export async function fetchDiscoverBrowse(
  params: DiscoverBrowseParams,
  shortlistContext: DiscoverShortlistContext,
): Promise<DiscoverCompaniesResult> {
  const { mode, page, limit, expoSignupSlug } = params;

  if (mode === "expoDirectory" && expoSignupSlug) {
    const response = await trpc.discover.listPublicExpoDirectory.query({
      slug: expoSignupSlug,
      page,
      limit,
    });
    const companies = mapListCompanies(response.companies || [], shortlistContext);
    return {
      companies,
      totalPages: response.meta?.totalPages || 1,
      matchCount: response.meta?.total ?? companies.length,
    };
  }

  if (mode === "facilitatorSearch") {
    const response = await trpc.facilitator.searchVerifiedCompanies.query({ page, limit });
    const companies = mapListCompanies(response.companies || [], shortlistContext);
    return {
      companies,
      totalPages: response.meta?.totalPages || 1,
      matchCount: response.meta?.total ?? companies.length,
    };
  }
  if (mode === "expertSearch") {
    const response = await trpc.expert.searchVerifiedCompanies.query({ page, limit });
    const companies = mapListCompanies(response.companies || [], shortlistContext);
    return {
      companies,
      totalPages: response.meta?.totalPages || 1,
      matchCount: response.meta?.total ?? companies.length,
    };
  }
  if (mode === "expoHeadSearch") {
    const response = await trpc.expo.searchVerifiedCompanies.query({ page, limit });
    const companies = mapListCompanies(response.companies || [], shortlistContext);
    return {
      companies,
      totalPages: response.meta?.totalPages || 1,
      matchCount: response.meta?.total ?? companies.length,
    };
  }
  if (mode === "ecosystemHeadSearch") {
    const response = await trpc.ecosystemHead.searchVerifiedCompanies.query({ page, limit });
    const companies = mapListCompanies(response.companies || [], shortlistContext);
    return {
      companies,
      totalPages: response.meta?.totalPages || 1,
      matchCount: response.meta?.total ?? companies.length,
    };
  }
  if (mode === "discover") {
    const response = await trpc.company.discover.query({ page, limit });
    const companies = mapListCompanies(response.companies || [], shortlistContext);
    return {
      companies,
      totalPages: response.pagination?.totalPages || 1,
      matchCount: response.pagination?.total ?? companies.length,
    };
  }

  const response = await trpc.company.listAll.query({ page, limit });
  const companies = mapListCompanies(response.companies || [], shortlistContext);
  return {
    companies,
    totalPages: response.pagination?.totalPages || 1,
    matchCount: response.pagination?.total ?? companies.length,
  };
}

export async function fetchDiscoverSearch(
  params: DiscoverSearchParams,
  shortlistContext: DiscoverShortlistContext,
): Promise<DiscoverCompaniesResult> {
  const { mode, page, limit, query, sectorSlug, expoSignupSlug } = params;
  const searchBy = deriveSearchByFromQuery(query);
  const effectiveQuery = searchBy === "gtid" ? query.trim().toUpperCase() : query.trim();
  const sectorForRequest = effectiveQuery ? null : sectorSlug;

  if (mode === "expoDirectory" && expoSignupSlug) {
    const response = await trpc.discover.listPublicExpoDirectory.query({
      slug: expoSignupSlug,
      search: effectiveQuery || undefined,
      page,
      limit,
    });
    const companies = mapListCompanies(response.companies || [], shortlistContext);
    return {
      companies,
      totalPages: response.meta?.totalPages || 1,
      matchCount: response.meta?.total ?? companies.length,
    };
  }

  if (
    mode === "facilitatorSearch" ||
    mode === "expertSearch" ||
    mode === "expoHeadSearch" ||
    mode === "ecosystemHeadSearch"
  ) {
    const response =
      mode === "facilitatorSearch"
        ? await trpc.facilitator.searchVerifiedCompanies.query({
            search: effectiveQuery || undefined,
            page,
            limit,
          })
        : mode === "expertSearch"
          ? await trpc.expert.searchVerifiedCompanies.query({
              search: effectiveQuery || undefined,
              page,
              limit,
            })
          : mode === "expoHeadSearch"
            ? await trpc.expo.searchVerifiedCompanies.query({
                search: effectiveQuery || undefined,
                page,
                limit,
              })
            : await trpc.ecosystemHead.searchVerifiedCompanies.query({
                search: effectiveQuery || undefined,
                page,
                limit,
              });
    const companies = mapListCompanies(response.companies || [], shortlistContext);
    return {
      companies,
      totalPages: response.meta?.totalPages || 1,
      matchCount: response.meta?.total ?? companies.length,
    };
  }

  const response = await trpc.company.quickSearch.query({
    query: effectiveQuery,
    page,
    limit,
    ...(sectorForRequest ? { sector: sectorForRequest } : {}),
    searchBy,
  });
  const companies = mapQuickSearchCompanies(response.companies || [], shortlistContext);
  return {
    companies,
    totalPages: response.pagination?.totalPages || 1,
    matchCount: response.pagination?.total ?? companies.length,
  };
}
